import { clearReliableTimeout } from 'mini-refresh-timers/lib/utils/reliabletimer';
import { getTimestamp, now } from 'owa-datetime';
import { isNullOrUndefined } from 'mini-common-utils/lib/utils/isNullOrUndefined';
import type { UpNextItem } from '../schema/UpNext.types';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type {
    UpNextStateDispatch,
    UpNextStaticProps,
    UpNextInfo,
} from '../schema/UpNextStateContext.types';

// State context
export const UpNextItemsListContext = createContext<UpNextItem[] | null>(null);
const UpNextInfoContext = createContext<UpNextInfo | null>(null);
const UpNextStaticObjectContext = createContext<React.MutableRefObject<UpNextStaticProps> | null>(
    null
);

// Dispatch State context
const UpNextStateDispatchContext = createContext<UpNextStateDispatch | null>(null);

export const useUpNextStateInfo = (): UpNextInfo => {
    const upNextInfo = useContext(UpNextInfoContext);
    return upNextInfo;
};

export const useUpNextStateItemsList = (): UpNextItem[] => {
    const upNextItemsList = useContext(UpNextItemsListContext);
    return upNextItemsList;
};

export const useUpNextStateStaticObject = (): React.MutableRefObject<UpNextStaticProps> => {
    const upNextStaticObject = useContext(UpNextStaticObjectContext);
    return upNextStaticObject;
};

export const useUpNextStateDispatch = (): UpNextStateDispatch => {
    const dispatch = useContext(UpNextStateDispatchContext);
    if (!dispatch) {
        throw new Error('Invoke useUpNextStateDispatch inside UpNextStateContextProvider');
    }
    return dispatch;
};

export const UpNextStateContextProvider = ({ children }) => {
    const [upNextItemsList, setUpNextItemsList] = useState<UpNextItem[]>(null);

    const [upNextInfo, setUpNextInfo] = useState<UpNextInfo>({
        upNextItem: null,
        remainingTime: null,
    });

    const upNextRef = useRef(null);
    const upNextStaticObject = useRef<UpNextStaticProps>({
        setupListener: false,
        expirationTimerId: null,
        upNextRef,
        payload: { mountTimestamp: getTimestamp(now()) },
        viewedUpNext: false,
        listViewScrolled: false,
    });

    const updateUpNextStaticObject = useCallback((updates: Partial<UpNextStaticProps>) => {
        if (!isNullOrUndefined(updates)) {
            upNextStaticObject.current = { ...upNextStaticObject.current, ...updates };
        }
    }, []);

    useEffect(
        () => () => {
            clearReliableTimeout(upNextStaticObject.current?.expirationTimerId);
        },
        []
    );

    const dispatchObject = useMemo(
        () => ({ setUpNextInfo, setUpNextItemsList, updateUpNextStaticObject }),
        [updateUpNextStaticObject]
    );

    return (
        <UpNextStateDispatchContext.Provider value={dispatchObject}>
            <UpNextStaticObjectContext.Provider value={upNextStaticObject}>
                <UpNextItemsListContext.Provider value={upNextItemsList}>
                    <UpNextInfoContext.Provider value={upNextInfo}>
                        {children}
                    </UpNextInfoContext.Provider>
                </UpNextItemsListContext.Provider>
            </UpNextStaticObjectContext.Provider>
        </UpNextStateDispatchContext.Provider>
    );
};
